import * as React from 'react';

import { BillStatus, enumBillStatus } from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter, ThemeColor } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import { $columnSelectedStatuses } from '~/modules/finance/_state/financeBills.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface StatusOption {
  id: BillStatus;
  label: string;
}

const billStatusColorMap: Record<BillStatus, ThemeColor> = {
  [enumBillStatus.Pending]: 'warning',
  [enumBillStatus.Paid]: 'success',
  [enumBillStatus.Voided]: 'failure',
};

interface BillStatusColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ BILL STATUS COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const BillStatusColumnFilter: React.FC<BillStatusColumnFilterProps> = ({
  onClose,
}) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedStatuses,
  );

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Memoized values
  const selectedStatuses = React.useMemo(
    () => Array.from(selectedStatusesSet),
    [selectedStatusesSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedStatuses),
    [selectedStatuses],
  );

  const statusOptions: StatusOption[] = React.useMemo(
    () => [
      {
        id: enumBillStatus.Pending,
        label: t.bills.list.status.options.pending,
      },
      { id: enumBillStatus.Paid, label: t.bills.list.status.options.paid },
      { id: enumBillStatus.Voided, label: t.bills.list.status.options.voided },
    ],
    [
      t.bills.list.status.options.pending,
      t.bills.list.status.options.paid,
      t.bills.list.status.options.voided,
    ],
  );

  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) => selectedStatuses.includes(option.id)),
    [statusOptions, selectedStatuses],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedStatusesSet(new Set(Array.from(keys) as BillStatus[]));
    },
    [setSelectedStatusesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
  }, [setSelectedStatusesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <FilterLabel
        label={item.label}
        color={billStatusColorMap[item.id]}
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@BillStatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.bills.list.search.placeholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
